import ConnectyCube from "connectycube";
import { Calls, CallType } from "connectycube/types";
import { useShallow } from "zustand/react/shallow";
import useCallsListeners from "./useCallsListeners";
import useCallsMediaManager, { CallsMediaManager } from "./useCallsMediaManager";
import useCallsStore, {
  ActiveMedia,
  CallsStoreSessionDataState,
  CallsStoreSessionState,
  CallDataType,
} from "./useCallsStore";
import { useCallback, useEffect } from "react";
import useCallsStoreRef from "./useCallsStoreRef";
import { CallsLogger } from "../helpers";

export interface CallsContext extends CallsStoreSessionState, CallsStoreSessionDataState, CallsMediaManager {
  startCall: (userID: number | number[], callType: CallType, extension?: Calls.UserInfo) => void;
  acceptCall: (extension?: Calls.UserInfo) => void;
  rejectCall: (extension?: Calls.UserInfo) => void;
  stopCall: (extension?: Calls.UserInfo) => void;
  resetSessionData: (type?: CallDataType) => void;
  participantsIds: number[];
}

const useCalls = (): CallsContext => {
  const mediaManager = useCallsMediaManager();
  const [
    activeCall,
    setActiveCall,
    incomingCall,
    resetIncomingCall,
    setLocalStream,
    userMediaParams,
    displayMediaParams,
    activeMedia,
    deviceChangeEvent,
    incomingCallCallData,
    incomingCallStopData,
    activeCallAcceptData,
    activeCallRejectData,
    activeCallStopData,
    activeCallNotAnswerData,
    activeCallConnectionStateData,
    resetSessionData,
    participantsIDs,
    setParticipantsIDs,
    resetStore,
  ] = useCallsStore(
    useShallow((store) => [
      store.activeCall,
      store.setActiveCall,
      store.incomingCall,
      store.resetIncomingCall,
      store.setLocalStream,
      store.userMediaParams,
      store.displayMediaParams,
      store.activeMedia,
      store.deviceChangeEvent,
      store.incomingCallCallData,
      store.incomingCallStopData,
      store.activeCallAcceptData,
      store.activeCallRejectData,
      store.activeCallStopData,
      store.activeCallNotAnswerData,
      store.activeCallConnectionStateData,
      store.resetSessionData,
      store.participantsIDs,
      store.setParticipantsIDs,
      store.resetStore,
    ]),
  );
  const hasActiveListenersRef = useCallsStoreRef("hasActiveListeners");
  const { setupListeners } = useCallsListeners();

  const startCall = async (
    userID: number | number[] = [],
    callType: CallType = CallType.VIDEO,
    extension?: Calls.UserInfo,
  ): Promise<void> => {
    const usersIDs = Array.isArray(userID) ? userID : [userID];
    const session = ConnectyCube.videochat.createNewSession(usersIDs, callType);

    try {
      resetSessionData();
      setParticipantsIDs(usersIDs);
      await _setLocalStreamAndStoreSession(session);
      session.call(extension);
    } catch (error) {
      CallsLogger.error("startCall", error);
      setParticipantsIDs([]);
    }
  };

  const acceptCall = useCallback(
    async (extension?: Calls.UserInfo) => {
      if (incomingCall) {
        const { currentUserID, initiatorID, opponentsIDs } = incomingCall;
        const participantsIds = [...opponentsIDs, initiatorID].filter((id) => id !== currentUserID);

        resetSessionData();
        setParticipantsIDs(participantsIds);

        try {
          await _setLocalStreamAndStoreSession(incomingCall);
          incomingCall.accept(extension);
          resetIncomingCall();
        } catch (error) {
          CallsLogger.error("acceptCall", error);
          incomingCall.stop(extension);
        } finally {
          resetIncomingCall();
        }
      }
    },
    [incomingCall],
  );

  const rejectCall = useCallback(
    (extension?: Calls.UserInfo) => {
      incomingCall?.reject(extension);
      resetIncomingCall();
    },
    [incomingCall],
  );

  const stopCall = useCallback(
    (extension?: Calls.UserInfo) => {
      resetStore();
      activeCall?.stop(extension);
    },
    [activeCall],
  );

  const _setLocalStreamAndStoreSession = useCallback(
    async (session: Calls.Session) => {
      const stream =
        activeMedia === ActiveMedia.DISPLAY
          ? await session.getDisplayMedia(displayMediaParams)
          : await session.getUserMedia(userMediaParams);

      if (session.callType === CallType.AUDIO) {
        stream.getVideoTracks().forEach((track) => (track.enabled = false));
      }

      setLocalStream(stream);
      setActiveCall(session);
    },
    [activeMedia, userMediaParams, displayMediaParams],
  );

  useEffect(() => {
    if (activeCall && participantsIDs.size === 0) {
      stopCall();
    }
  }, [activeCall, participantsIDs]);

  useEffect(() => {
    if (!hasActiveListenersRef.current) {
      setupListeners();
    }
  }, []);

  return {
    startCall,
    acceptCall,
    rejectCall,
    stopCall,
    activeCall,
    incomingCall,
    deviceChangeEvent,
    incomingCallCallData,
    incomingCallStopData,
    activeCallAcceptData,
    activeCallRejectData,
    activeCallStopData,
    activeCallNotAnswerData,
    activeCallConnectionStateData,
    resetSessionData,
    participantsIds: [...participantsIDs],
    ...mediaManager,
  };
};

export default useCalls;
